import { Link } from "react-router-dom";

const SIZES = {
  sm: { px: "w-7 h-7", text: "text-[10px]" },
  md: { px: "w-10 h-10", text: "text-xs" },
  lg: { px: "w-20 h-20", text: "text-lg" },
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Reusable byline avatar.
 * - Renders the author's `avatar_url` (resolved against REACT_APP_BACKEND_URL
 *   when it starts with `/` so we never load the wrong host)
 * - Falls back to initials in a brand-colored circle on image error
 * - When `linkToProfile` is true, wraps the avatar in a Link to
 *   `/blog/author/{slug}` for cross-page navigation
 */
export default function AuthorAvatar({ author, size = "md", linkToProfile = false, className = "" }) {
  const dims = SIZES[size] || SIZES.md;
  const src = author?.avatar_url
    ? author.avatar_url.startsWith("http")
      ? author.avatar_url
      : `${BACKEND}${author.avatar_url}`
    : null;
  const initials = (author?.name || "?")
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const inner = (
    <span
      className={`inline-flex items-center justify-center rounded-full overflow-hidden bg-slate-900 text-white font-bold shrink-0 ${dims.px} ${dims.text} ${className}`}
      data-testid={`author-avatar-${author?.slug || "unknown"}`}
      aria-label={author?.name}
    >
      {src ? (
        <img
          src={src}
          alt={author?.name || ""}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Hide the broken img so the initials behind it show through.
            e.currentTarget.style.display = "none";
          }}
        />
      ) : null}
      {!src && <span>{initials}</span>}
    </span>
  );

  if (linkToProfile && author?.slug) {
    return (
      <Link
        to={`/blog/author/${author.slug}`}
        className="inline-block hover:opacity-80 transition-opacity"
        title={`More from ${author.name}`}
      >
        {inner}
      </Link>
    );
  }
  return inner;
}
